import React, {useEffect, useMemo, useContext, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import { currentLang, currentLangParam, currentLongLang, getHealthCheck } from '../../components/components/utils';
import AppContext from '../../components/AppContext';
import { API, Auth } from 'aws-amplify';
import {constants} from '../../constants'
import Spinner from '../../components/Spinner';
import PopupLink from '../../templates/PopupLink';
function Callback({navigation}) {
	const appContext = useContext(AppContext);

	const labels = useMemo(() => {
		const formatValues = {
			p: (chunks) => <p>{chunks}</p>,
			a: (chunks) => (
				<a
					href={`https://www.cbsa-asfc.gc.ca/contact/bis-sif-${document.documentElement.lang === "fr" ? "fra" : "eng"}.html`}
					className="App-link"
					target="_blank"
					rel="noreferrer"
				>
					{chunks}
				</a>
			),
		};

		return {
			"http.networkError": (
				<FormattedMessage
					id="http.networkError"
					values={formatValues}
				/>
			),
			"http.networkErrorFileServer": (
				<FormattedMessage
					id="http.networkErrorFileServer"
					values={formatValues}
				/>
			),
			"http.networkError.args": (errorNum) => (
				<FormattedMessage
					id="http.networkError.args"
					values={{
						...formatValues,
						num: errorNum ? `01-0001-${errorNum}` : "01-0001-Unknown",

					}}
				/>
			),
			"http.networkError.03-015": (errorNum) => (
				<FormattedMessage
					id="http.networkError.args"
					values={{
						...formatValues,
						num: errorNum ? `03-015-${errorNum}` : "03-015-Unknown",

					}}
				/>
			), // preliminaryData lambda error
			"http.networkError.03-022": (errorNum) => (
				<FormattedMessage
					id="http.networkError.args"
					values={{
						...formatValues,
						num: errorNum ? `03-022-${errorNum}` : "03-022-Unknown",

					}}
				/>
			), // updateContactInfoFromWURM lambda error
		};
	}, []); // Empty dependency array since formatValues is static and doesn't depend on intl

	const [showSpinner, setShowSpinner] = useState(true);

	useEffect(() => {

		//run these functions in parallel, but wait for both of them to complete
		async function fetchData() {
			Promise.all([
				loadPreliminaryData()
				, updateWURMContactDataInGCCase()
				, getHealthCheck(appContext, labels["http.networkErrorFileServer"], true)
				, openWebSocket()
			]).then((redirectKey) => {
				
				if (!!redirectKey[0] || !!redirectKey[1]) {
					navigation.navigate(redirectKey[0] || redirectKey[1], currentLangParam());
				} else {
					const urlParameters = localStorage.getItem("urlParameters");
					const paramDict = {};
					if (urlParameters !== null) {
						const paramList = urlParameters.split('&');
						for (const param of paramList) {
							const splitParam = param.split('=');
							paramDict[splitParam[0]] = splitParam[1]?.replaceAll("+", " ");
						}
					}
					try {
						if (!!localStorage.getItem("activeKey")) {
							let searchParams = new URLSearchParams(document.location.search);
							Object.keys(paramDict).forEach((paramKey) => {
								if (!searchParams.get(paramKey)) {
									searchParams.set(paramKey, paramDict[paramKey]);
								}
							})
							navigation.navigate(localStorage.getItem("activeKey"), paramDict);
						} else {
							navigation.navigate("Menu", paramDict);
						}
					} catch (e) {
						navigation.navigate("Menu", paramDict);
					}
				}
			}).finally(() => {
				setShowSpinner(false);
			});
		}

		fetchData();

	}, []);


	function loadPreliminaryData() {
		let apiURL = `preliminaryData?lang=${currentLang()}`;

		return API.get("backend-api", apiURL, {}).then(function(response){
			setPreliminaryData(!!response.codeTables ? response.codeTables : window.env["codeTables"]);
		}).catch(function(error){
			if (error === "No current user" || error === "The user is not authenticated") {
				return "Login";
			}
			if (window.env.environment !== "prod") {
				console.log({error})
			}
			if (window.env.environment !== "local") {
				let errorNum = error?.response?.data?.Error?.ErrorNumber; // ErrorNumber specific to the lambda

				if (!!errorNum) {
					appContext.setFlashError({duration:-1, messages:[labels["http.networkError.03-015"](errorNum)]});
				} else {
					appContext.setFlashError({duration:-1, messages:[labels["http.networkError"]]});
				}
			} else {
				setPreliminaryData(window.env["codeTables"]);	//local env, fill with dummy data
			}
		});
	}

	function updateWURMContactDataInGCCase() {

		let apiURLWURM = `updateContactInfoFromWURM`;

		return API.post("backend-api", apiURLWURM, {}).then(function(response){
			setContactData(response.contactData);
			setAccountData(response.accountData);
			appContext.setExistingUser(response.existingUser);

			//defaulting the UI to the language set in the user's language preference if the user doesn't have a language specified in the querystring
			if (!("lang" in currentLangParam())) {
				document.documentElement.lang = constants.languagePreferenceGUIDtoLang[response?.contactData?.languagePreference ?? constants.languagePreferenceGUID["English"]] ?? "en";
			}

			if (!response.existingUser) {
				return "Account_Edit";
			}
		}).catch(function(error){
			if (error === "No current user" || error === "The user is not authenticated") {
				return "Login";
			}
			if (window.env.environment !== "prod") {
				console.log("ERROR in updateWURMContactInfoInGCCase")
				console.log({error})
			}
			if (window.env.environment === "local") {
				//local env, fill with dummy data
				setContactData(window.env["contactData"]);
				setAccountData(window.env["accountData"]);
				appContext.setExistingUser(window.env["existingUser"]);
				if (!window.env["existingUser"]) {
					return "Account_Edit";
				} else {
					return localStorage.getItem("activeKey");
				}
			} else {
				let errorNum = error?.response?.data?.Error?.ErrorNumber; // ErrorNumber specific to the lambda

				if (!!errorNum) {
					appContext.setFlashError({duration:-2, messages:[labels["http.networkError.03-022"](errorNum)]});
				} else {
					appContext.setFlashError({duration:-2, messages:[labels["http.networkError"]]});
				}
				return "Login";
			}
		});
	}

	function setPreliminaryData(codeTables) {

		appContext.setCodeTables(codeTables);

		createCodeTableLookup(codeTables);

	}

	function setContactData(newContactData) {
		let mergedContactData = appContext.contactData || {};

		mergedContactData.ace_urn = newContactData.ace_urn;
		mergedContactData.emailaddress1 = newContactData.emailaddress1;
		mergedContactData.firstname = newContactData.firstname;
		mergedContactData.lastname = newContactData.lastname;
		mergedContactData.languagePreference = newContactData.languagePreference;

		appContext.setContactData(mergedContactData);
	}

	function setAccountData(newAccountData) {
		let mergedAccountData = appContext.accountData || {};

		appContext.setAccountData(mergedAccountData);
	}

	function createCodeTableLookup(codeTables) {
		let codeTableLookup = {}

		Object.keys(codeTables).forEach(key => {
			let codeTableLookupObject = {}

			if (Array.isArray(codeTables[key])) {
				codeTables[key].forEach((elementObj) => {
					codeTableLookupObject[elementObj.value] = elementObj.label;
					codeTableLookupObject[elementObj.mdmCode] = elementObj.label;
				})
			}
			codeTableLookup[key] = codeTableLookupObject;
		})

		if (!!codeTableLookup.provinces || !!codeTableLookup.states || codeTableLookup.mexicanStates) {
			let allProvincesAndStates = {};
			if (!!codeTableLookup.provinces) {
				allProvincesAndStates = {...allProvincesAndStates, ...codeTableLookup.provinces};
			}
			if (!!codeTableLookup.states) {
				allProvincesAndStates = {...allProvincesAndStates, ...codeTableLookup.states};
			}
			if (!!codeTableLookup.mexicanStates) {
				allProvincesAndStates = {...allProvincesAndStates, ...codeTableLookup.mexicanStates};
			}
			codeTableLookup.allProvincesAndStates = allProvincesAndStates;
		}

		appContext.setCodeTableLookup(codeTableLookup);
	}
	
	async function openWebSocket() {
		const currentSession = await Auth.currentSession().catch((error) => {
			if (error === "No current user" || error === "The user is not authenticated") {
				return "Login";
			}
		});
		
		const urn = currentSession?.accessToken?.payload?.userAttributes?.["custom:URN"]
		const idToken = currentSession?.idToken?.jwtToken;

		if (!!urn && !!idToken) {
			connectToEventAPI(urn, idToken);
		}
	}
	
	function base64EncodeURISafe(str) {
		// Convert the string to Base64
		const base64Encoded = btoa(str);

		// Replace unsafe characters to make it URI-safe
		return base64Encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
	}
	
	function connectToEventAPI(urn, idToken) {
		let websocket = null;
		
		const config = {
			APPSYNC_ENDPOINT: window.env.websocketConfig.apiEndpoint,
			APPSYNC_REALTIME_ENDPOINT: window.env.websocketConfig.realtimeEndpoint,
			CHANNEL_NAMESPACE: window.env.websocketConfig.channelNamespace,
			SUBSCRIBER_ID: urn,
			JWT_TOKEN: idToken
		};

		// Validate inputs
		if (!config.APPSYNC_ENDPOINT || !config.APPSYNC_REALTIME_ENDPOINT ||
			!config.CHANNEL_NAMESPACE || !config.SUBSCRIBER_ID || !config.JWT_TOKEN) {
			if (window.env.environment !== "prod" && window.env.environment !== "test") {
				console.log('Please fill in all fields', 'error');
			}
			return;
		}

		// Create the header object
		const header = {
			host: config.APPSYNC_ENDPOINT,
			Authorization: config.JWT_TOKEN,
			"User-Agent": navigator.userAgent
		};

		// Encode the header safely
		const headerString = base64EncodeURISafe(JSON.stringify(header));
		const protocolHeader = `header-${headerString}`;

		// Create WebSocket URL
		const wsUrl = `wss://${config.APPSYNC_REALTIME_ENDPOINT}/event/realtime`;

		if (window.env.environment !== "prod" && window.env.environment !== "test") {
			console.log(`Connecting to ${wsUrl}...`);
		}

		// Create WebSocket connection
		websocket = new WebSocket(wsUrl, ['aws-appsync-event-ws', protocolHeader]);

		websocket.onopen = () => {
			if (window.env.environment !== "prod" && window.env.environment !== "test") {
				console.log('WebSocket connected', 'success');
			}

			// Send subscription message
			const subscriptionMessage = {
				id: crypto.randomUUID(), // Generates a unique ID
				type: "subscribe",
				channel: `${config.CHANNEL_NAMESPACE}/${config.SUBSCRIBER_ID}`,
				authorization: {
					host: config.APPSYNC_ENDPOINT,
					Authorization: config.JWT_TOKEN,
					"User-Agent": navigator.userAgent
				}
			};

			if (window.env.environment !== "prod" && window.env.environment !== "test") {
				console.log('Sending subscription request...');
				console.log({subscriptionMessage})
			}
			websocket.send(JSON.stringify(subscriptionMessage));
		};


		websocket.onmessage = (event) => {
			try {
				const data = JSON.parse(event.data);
				if (window.env.environment !== "prod" && window.env.environment !== "test") {
					console.log({data});
				}
				// Handle subscription success
				if (data.type === 'subscribe_success') {
					if (window.env.environment !== "prod" && window.env.environment !== "test") {
						console.log('Successfully subscribed to channel', 'success');
					}
				}
				// Handle keep-alive messages
				else if (data.type === 'ka') {
					if (window.env.environment !== "prod" && window.env.environment !== "test") {
						console.log('Keep-alive received');
					}
				}
				// Handle data messages
				else if (data.type === 'data') {
					const responseMessage = JSON.parse(data.event).message;
					if (window.env.environment !== "prod" && window.env.environment !== "test") {
						console.log({responseMessage});
					}
					const downloadUri = responseMessage.downloadUri;
					const transferSuccessFileName = responseMessage.transferSuccessFileName; 
					//handle download link
					if (!!downloadUri) {
						
						const encodedFileName = downloadUri.substr(downloadUri.lastIndexOf('/') + 1, downloadUri.indexOf('?') - downloadUri.lastIndexOf('/') - 1);
						const fileName =  decodeURIComponent(encodedFileName);
						appContext.setFlashSuccess({duration:-1, messages:[<FormattedMessage id="fetch.transferPreparedAndAvailable" values={{p: (chunks) => <p>{chunks}</p>, b: (chunks) => <b>{chunks}</b>, fileName: fileName, a: (chunks) => <a id={"download-"+encodedFileName} href={downloadUri} rel="noreferrer" download>{chunks}</a>}} />]});
						
						setTimeout(document.getElementById("download-"+encodedFileName).click(), 1000);
					}
					//handle transfer success message
					if (!!transferSuccessFileName) {
						const decodedFileName = decodeURIComponent(transferSuccessFileName);
						appContext.setFlashSuccess({duration:-1, messages:[<FormattedMessage id="fetch.uploadValidationSuccessfulAndTransferComplete" values={{p: (chunks) => <p>{chunks}</p>, b: (chunks) => <b>{chunks}</b>, fileName: decodedFileName}} />]});
					}
					
				}
			} catch (e) {
				if (window.env.environment !== "prod" && window.env.environment !== "test") {
					console.log(event.data);
				}
			}
		};

		websocket.onclose = () => {
			if (window.env.environment !== "prod" && window.env.environment !== "test") {
				console.log('WebSocket disconnected');
			}
			websocket = null;
			appContext.setWebsocket(null);
		};

		websocket.onerror = (error) => {
			console.log(`Error: ${error.message}`, 'error');
		};
		
		appContext.setWebsocket(websocket);

		if (window.env.environment !== "prod" && window.env.environment !== "test") {
			console.log("new websocket connection created!");
		}
	}
	
	return (
		<>
		  <Spinner show={showSpinner} />
		</>
	);
}

Callback.path = "callback";
Callback.navigationOptions = {
	pageTitle: ""
	, breadcrumb: [
		<PopupLink url={window.env["canada-url"][currentLang()]}><FormattedMessage id="breadcrumb.Canada.text" /></PopupLink>
		, <PopupLink url={window.env["cbsa-breadcrumbs-url"][currentLang()]}><FormattedMessage id="breadcrumb.CBSA.text" /></PopupLink>
		, <PopupLink url={window.env["cbsa-sima-breadcrumbs-url"][currentLang()]}><FormattedMessage id="breadcrumb.ACE.text" /></PopupLink>
	]
	, breadcrumbTitle: <FormattedMessage id="breadcrumbs.acePortal"/>
};

export default Callback;